import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchListPhones,
  getCallsPage,
  getDialerHeatmap,
  getDialerNumbers,
  getDialerPerformance,
  getListCompletion,
  getQualificationAccuracy,
  type DialerFilters,
  type Grain,
} from '@/api/endpoints/analytics';

/**
 * The Performances page's data (W4): one query per server aggregate. Each
 * keeps the previous result while a filter change loads (no flash of zeros),
 * and none polls: the figures cover a period, a 3 s refresh bought nothing.
 */

const STALE = 30_000;
const base = ['dialer-analytics'] as const;

export function useDialerPerformance(body: DialerFilters & { conversation_threshold: number; grain: Grain; compare_from?: string; compare_to?: string }) {
  return useQuery({
    queryKey: [...base, 'performance', body],
    queryFn: ({ signal }) => getDialerPerformance(body, signal),
    staleTime: STALE,
    placeholderData: keepPreviousData,
  });
}

export function useDialerHeatmap(body: DialerFilters, enabled = true) {
  return useQuery({
    queryKey: [...base, 'heatmap', body],
    queryFn: ({ signal }) => getDialerHeatmap(body, signal),
    staleTime: STALE,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDialerNumbers(body: DialerFilters, enabled = true) {
  return useQuery({
    queryKey: [...base, 'numbers', body],
    queryFn: ({ signal }) => getDialerNumbers(body, signal),
    staleTime: STALE,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useListCompletion(body: { agent_email?: string; list?: string; customer_id?: string; max_attempts?: number }, enabled = true) {
  return useQuery({
    queryKey: [...base, 'list-completion', body],
    queryFn: ({ signal }) => getListCompletion(body, signal),
    staleTime: STALE,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useQualificationAccuracy(body: { from?: string; to?: string; customer_id?: string }, enabled = true) {
  return useQuery({
    queryKey: [...base, 'accuracy', body],
    queryFn: ({ signal }) => getQualificationAccuracy(body, signal),
    staleTime: STALE,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** The phones of the selected list, for the queries that filter calls themselves (drill-down, export). */
export function useListPhones(list: string | undefined, customerId: string | undefined) {
  return useQuery({
    queryKey: [...base, 'list-phones', list, customerId],
    queryFn: ({ signal }) => fetchListPhones(list!, customerId, signal),
    enabled: !!list,
    staleTime: 5 * 60_000,
  });
}

/** The calls behind a tile, 50 at a time with « Charger plus » (no silent cap). */
export function useDrillCalls(filter: Record<string, unknown> | null) {
  return useInfiniteQuery({
    queryKey: [...base, 'drill', filter],
    queryFn: ({ pageParam, signal }) => getCallsPage({ filter: filter ?? {}, limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }, signal),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!filter,
  });
}
